import React from 'react';
import {
  WorkspaceFilterBarSkeleton,
  WorkspaceTableSkeleton,
  WorkspaceTabsSkeleton,
  type WorkspaceTableSkeletonColumn,
} from '@/components/reusable/WorkspaceSkeleton';

/**
 * The single loading state for the Campaigns workspace (`/home/campaigns`).
 *
 * Opening Campaigns used to flash four loaders in sequence before the table
 * appeared:
 *   1. a full-page "Loading..." — the `FeatureRoute` / `RoleRoute` gates
 *   2. a bare spinner — the route-level `<Suspense>` fallback in `App.tsx`
 *   3. a centred "Loading business accounts…" spinner — `NewCampaigns` waiting
 *      on the accounts query
 *   4. a card-grid of `EntityGridCardSkeleton` — the list query, a shape the
 *      default list view never has
 *
 * All four now render this skeleton, so the layout appears once and stays put
 * until the real workspace replaces it. The columns mirror `NewCampaignsBoard`
 * in list view (the desktop default).
 */

const CAMPAIGNS_TABLE_COLUMNS: WorkspaceTableSkeletonColumn[] = [
  { width: 'w-[40%]' },
  { width: 'w-16' },
  { width: 'w-12' },
  { width: 'w-16' },
  { width: 'w-12' },
  { width: 'w-14' },
  { width: 'w-16' },
  { width: 'w-20' },
];

/** The campaigns table on its own — used while the list loads inside the page. */
export const CampaignsTableSkeleton: React.FC<{ rows?: number }> = ({ rows = 8 }) => (
  <WorkspaceTableSkeleton
    columns={CAMPAIGNS_TABLE_COLUMNS}
    rows={rows}
    data-testid="campaigns-table-skeleton"
  />
);

/** Tabs strip: Campaigns, Templates. */
export const CampaignsTabsSkeleton: React.FC = () => <WorkspaceTabsSkeleton widths={[84, 76]} />;

/** Toolbar: search, Status, then view toggle and New Campaign. */
export const CampaignsFilterBarSkeleton: React.FC = () => (
  <WorkspaceFilterBarSkeleton filters={1} actions={[72, 136]} />
);

/**
 * The page-level placeholder, used by the route guards and the `<Suspense>`
 * fallback. It carries the same chrome the loaded page does, so the workspace
 * does not paint twice — once bare, then again with tabs and a toolbar on top.
 */
export const CampaignsPageSkeleton: React.FC = () => (
  <div
    className="flex h-full min-h-full min-w-0 flex-col bg-white"
    aria-busy="true"
    aria-label="Loading campaigns"
    data-testid="campaigns-page-skeleton"
  >
    <CampaignsTabsSkeleton />
    <CampaignsFilterBarSkeleton />
    <div className="min-h-0 min-w-0 flex-1 overflow-hidden px-[15px] pb-4">
      <CampaignsTableSkeleton />
    </div>
  </div>
);

export default CampaignsPageSkeleton;
